import React, { useEffect, useMemo, useState } from 'react';
import { Download, Eye, Play, Sparkles, Upload } from 'lucide-react';
import { adminApi } from '../utils/api';

const SOURCE_TYPES = ['video_transcript', 'blog_post', 'youtube_url', 'raw_notes'];

const REPURPOSE_TYPES = new Set([
  'short_script',
  'youtube_long_script',
  'linkedin_carousel_outline',
  'blog_post',
  'newsletter_section',
  'twitter_thread',
  'instagram_caption_set',
  'youtube_description',
]);

const RepurposeManager = ({ tools, queueItems, onRefreshQueue, onMarkPublished }) => {
  const [running, setRunning] = useState(false);
  const [events, setEvents] = useState([]);
  const [jobSummary, setJobSummary] = useState(null);
  const [activeJobId, setActiveJobId] = useState('');
  const [outputs, setOutputs] = useState([]);
  const [jobHistory, setJobHistory] = useState([]);
  const [scheduledDate, setScheduledDate] = useState('');
  const [filters, setFilters] = useState({ status: '', platform: '', content_type: '' });
  const [workingId, setWorkingId] = useState('');
  const [form, setForm] = useState({
    source_type: 'raw_notes',
    source_content: '',
    tool_id: tools?.[0]?.id || '',
    week_theme: 'ROI Week',
  });

  const fallbackRows = useMemo(() => (
    (queueItems || [])
      .filter((row) => row.repurpose_job_id || REPURPOSE_TYPES.has(String(row.content_type || '').toLowerCase()))
      .sort((a, b) => new Date(b.created_at || b.updated_at || 0) - new Date(a.created_at || a.updated_at || 0))
  ), [queueItems]);

  const repurposeRows = outputs.length ? outputs : fallbackRows;

  const loadJobHistory = async () => {
    try {
      const res = await adminApi.listRepurposeJobs(30);
      setJobHistory(res?.data?.items || []);
    } catch (error) {
      console.error('Failed to load repurpose jobs:', error);
    }
  };

  useEffect(() => {
    loadJobHistory();
  }, []);

  const selectJob = async (jobId) => {
    if (!jobId) {
      setActiveJobId('');
      setOutputs([]);
      setJobSummary(null);
      return;
    }

    setActiveJobId(jobId);
    try {
      const [statusRes, outputsRes] = await Promise.all([
        adminApi.getRepurposeJobStatus(jobId),
        adminApi.getRepurposeOutputs(jobId),
      ]);
      setJobSummary(statusRes?.data?.job?.summary || null);
      setOutputs(outputsRes?.data?.items || []);
    } catch (error) {
      console.error('Failed to load selected repurpose job:', error);
      setOutputs([]);
      setJobSummary(null);
    }
  };

  const parseSlidesFromOutline = (row) => {
    const raw = row?.content?.outline || row?.content?.script || row?.content?.article || '';
    const lines = String(raw)
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .slice(0, 7);

    return Array.from({ length: 7 }).map((_, idx) => ({
      headline: lines[idx] || `Slide ${idx + 1}`,
      body: lines[idx + 1] || `Key insight ${idx + 1}`,
      metric: idx === 6 ? 'CTA' : `+${(idx + 2) * 10}%`,
    }));
  };

  const downloadText = (row) => {
    const payload = row?.content || row;
    const text = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${row.tool_name || 'tool'}-${row.content_type || 'content'}-${row.id || 'item'}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const streamProgress = async (jobId) => {
    const token = localStorage.getItem('authToken');
    const url = adminApi.getRepurposeProgressUrl(jobId);
    // url should be relative for proxy compatibility
    const response = await fetch(url.replace('http://localhost:8001', '/api').replace('http://localhost:8002', '/orchestrator'), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok || !response.body) return;

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split('\n\n');
      buffer = chunks.pop() || '';

      chunks.forEach((chunk) => {
        const line = chunk.split('\n').find((l) => l.startsWith('data: '));
        if (!line) return;
        try {
          const data = JSON.parse(line.replace('data: ', ''));
          setEvents((prev) => [...prev, data]);
        } catch {
          // Ignore malformed event
        }
      });
    }
  };

  const runRepurpose = async (e) => {
    e.preventDefault();
    if (!form.tool_id || !form.source_content.trim()) {
      alert('Select a tool and provide source content.');
      return;
    }

    setRunning(true);
    setEvents([]);
    setJobSummary(null);
    try {
      const res = await adminApi.repurposeContent(form);
      const jobId = res?.data?.job_id;
      setActiveJobId(jobId || '');
      setJobSummary(null);
      if (jobId) {
        await streamProgress(jobId);
        const statusRes = await adminApi.getRepurposeJobStatus(jobId).catch(() => null);
        setJobSummary(statusRes?.data?.job?.summary || null);
        const outputsRes = await adminApi.getRepurposeOutputs(jobId).catch(() => null);
        setOutputs(outputsRes?.data?.items || []);
      }
      await onRefreshQueue();
      await loadJobHistory();
    } catch (error) {
      console.error('Repurpose run failed:', error);
      alert('Repurpose pipeline failed.');
    } finally {
      setRunning(false);
    }
  };

  const makeCarouselMedia = async (row) => {
    setWorkingId(row.id);
    try {
      const payload = {
        tool_id: row.tool_id,
        carousel_type: 'tool_spotlight',
        slides_data: parseSlidesFromOutline(row),
      };
      const res = await adminApi.generateCarouselMedia(payload);
      const url = res?.data?.result?.pdf_url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      await onRefreshQueue();
    } catch (error) {
      console.error('Carousel generation failed:', error);
      alert('Failed to generate carousel PDF.');
    } finally {
      setWorkingId('');
    }
  };

  const previewCarousel = async (row) => {
    setWorkingId(row.id);
    try {
      const res = await adminApi.getCarouselPreview(row.id);
      const url = res?.data?.preview?.pdf_url;
      if (!url) {
        alert('No carousel preview available yet. Generate media first.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Preview fetch failed:', error);
      alert('No carousel preview available for this content yet.');
    } finally {
      setWorkingId('');
    }
  };

  const optimizeYouTube = async (row) => {
    setWorkingId(row.id);
    try {
      await adminApi.optimizeYouTube({
        tool_id: row.tool_id,
        video_type: 'review',
        hook_style: row.content?.hook_style || 'outcome',
        target_audience: 'B2B operators',
        content_id: row.id,
      });
      alert('YouTube metadata optimized and saved.');
    } catch (error) {
      console.error('YouTube optimization failed:', error);
      alert('Failed to optimize YouTube metadata.');
    } finally {
      setWorkingId('');
    }
  };

  const publishItem = async (row) => {
    setWorkingId(row.id);
    try {
      await adminApi.publishItem(row.id).catch(() => onMarkPublished(row.id));
      await onRefreshQueue();
    } catch (error) {
      console.error('Publish failed:', error);
      alert('Failed to publish this item.');
    } finally {
      setWorkingId('');
    }
  };

  const refreshOutputs = async () => {
    if (!activeJobId) return;
    try {
      const params = {
        ...(filters.status ? { status: filters.status } : {}),
        ...(filters.platform ? { platform: filters.platform } : {}),
        ...(filters.content_type ? { content_type: filters.content_type } : {}),
      };
      const res = await adminApi.getRepurposeOutputs(activeJobId, params);
      setOutputs(res?.data?.items || []);
    } catch (error) {
      console.error('Failed to refresh repurpose outputs:', error);
    }
  };

  const publishAllForJob = async () => {
    if (!activeJobId) {
      alert('Run or select an active repurpose job first.');
      return;
    }
    try {
      await adminApi.publishRepurposeOutputs(activeJobId);
      await Promise.all([refreshOutputs(), onRefreshQueue()]);
      alert('All generated outputs for this job were marked published.');
    } catch (error) {
      console.error('Bulk publish failed:', error);
      alert('Failed to publish all outputs for this job.');
    }
  };

  const scheduleAllForJob = async () => {
    if (!activeJobId) {
      alert('Run or select a repurpose job first.');
      return;
    }
    try {
      await adminApi.scheduleRepurposeOutputs(activeJobId, scheduledDate || null);
      await Promise.all([refreshOutputs(), onRefreshQueue()]);
      alert('Outputs were scheduled for publishing.');
    } catch (error) {
      console.error('Schedule-all failed:', error);
      alert('Failed to schedule outputs for this job.');
    }
  };

  const retryFailedForJob = async () => {
    if (!activeJobId) {
      alert('Run or select a repurpose job first.');
      return;
    }
    try {
      const res = await adminApi.retryFailedRepurposeOutputs(activeJobId);
      await Promise.all([refreshOutputs(), onRefreshQueue(), loadJobHistory()]);
      const data = res?.data || {};
      alert(`Retry completed. Republished: ${data.republished || 0}, reset to generated: ${data.reset_to_generated || 0}`);
    } catch (error) {
      console.error('Retry failed outputs failed:', error);
      alert('Failed to retry failed outputs for this job.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="terminal-grid rounded-none p-5">
        <h3 className="text-lg text-white font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
          Repurpose Pipeline Control Center
        </h3>

        <form onSubmit={runRepurpose} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label htmlFor="repurpose-source-type" className="text-sm text-text-secondary block mb-2">Source type</label>
              <select
                id="repurpose-source-type"
                className="input-pro rounded-none"
                value={form.source_type}
                onChange={(e) => setForm((f) => ({ ...f, source_type: e.target.value }))}
              >
                {SOURCE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="repurpose-tool-id" className="text-sm text-text-secondary block mb-2">Tool</label>
              <select
                id="repurpose-tool-id"
                className="input-pro rounded-none"
                value={form.tool_id}
                onChange={(e) => setForm((f) => ({ ...f, tool_id: e.target.value }))}
              >
                <option value="">-- select tool --</option>
                {(tools || []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="repurpose-week-theme" className="text-sm text-text-secondary block mb-2">Week theme</label>
              <input
                id="repurpose-week-theme"
                className="input-pro rounded-none"
                value={form.week_theme}
                onChange={(e) => setForm((f) => ({ ...f, week_theme: e.target.value }))}
                placeholder="e.g. ROI Proof Week"
              />
            </div>
          </div>

          <div>
            <label htmlFor="repurpose-source-content" className="text-sm text-text-secondary block mb-2">Source content</label>
            <textarea
              id="repurpose-source-content"
              className="input-pro rounded-none w-full h-32 resize-y"
              value={form.source_content}
              onChange={(e) => setForm((f) => ({ ...f, source_content: e.target.value }))}
              placeholder="Paste transcript, blog text, notes, or YouTube URL based on source type..."
            />
          </div>

          <button type="submit" disabled={running} className="btn-primary rounded-none inline-flex items-center gap-2">
            <Sparkles size={14} /> {running ? 'Running pipeline...' : 'Run repurposing pipeline'}
          </button>
        </form>

        {jobSummary && (
          <div className="mt-4 p-3 border border-cyber-yellow/30 bg-cyber-yellow/5 text-sm">
            <div className="text-cyber-yellow font-semibold">Pipeline summary</div>
            <div className="text-text-secondary mt-1">Generated {jobSummary.total_generated} pieces.</div>
            <div className="text-text-secondary">Estimated reach: {jobSummary.estimated_reach}</div>
          </div>
        )}

        {events.length > 0 && (
          <div className="mt-4 border border-white/10 p-3 max-h-40 overflow-y-auto text-xs font-mono bg-black/30">
            {events.map((ev, idx) => (
              <div key={`${idx}-${ev?.updated_at || ''}`} className="text-text-secondary py-1 border-b border-white/5">
                {ev?.event?.message || ev?.status || 'update'}
              </div>
            ))}
          </div>
        )}

        {activeJobId && (
          <div className="mt-3 text-xs text-text-secondary font-mono">
            Active Job: <span className="text-cyber-yellow">{activeJobId}</span>
          </div>
        )}

        {jobHistory.length > 0 && (
          <div className="mt-3">
            <label htmlFor="repurpose-job-select" className="text-sm text-text-secondary block mb-2">Load previous job</label>
            <select
              id="repurpose-job-select"
              className="input-pro rounded-none"
              value={activeJobId}
              onChange={(e) => selectJob(e.target.value)}
            >
              <option value="">-- select job --</option>
              {jobHistory.map((job) => (
                <option key={job.job_id} value={job.job_id}>
                  {job.job_id} - {job.status} - {new Date(job.updated_at || 0).toLocaleString()}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="terminal-grid rounded-none p-5">
        <h3 className="text-lg text-white font-semibold mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
          Repurpose Output Manager
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <input
            className="input-pro rounded-none"
            placeholder="Status filter"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          />
          <input
            className="input-pro rounded-none"
            placeholder="Platform filter"
            value={filters.platform}
            onChange={(e) => setFilters((f) => ({ ...f, platform: e.target.value }))}
          />
          <input
            className="input-pro rounded-none"
            placeholder="Type filter"
            value={filters.content_type}
            onChange={(e) => setFilters((f) => ({ ...f, content_type: e.target.value }))}
          />
          <div className="flex gap-2">
            <button onClick={refreshOutputs} className="btn-secondary rounded-none text-xs">Apply filters</button>
            <button onClick={publishAllForJob} className="btn-primary rounded-none text-xs">Publish all</button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
          <input
            type="date"
            className="input-pro rounded-none"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
          />
          <button onClick={scheduleAllForJob} className="btn-secondary rounded-none text-xs">Schedule all</button>
          <button onClick={retryFailedForJob} className="btn-secondary rounded-none text-xs">Retry failed</button>
          <button onClick={loadJobHistory} className="btn-secondary rounded-none text-xs">Refresh jobs</button>
        </div>

        <div className="space-y-2">
          {repurposeRows.length === 0 && (
            <div className="text-text-secondary text-sm">No repurposed outputs yet. Run the pipeline to generate one-to-many assets.</div>
          )}

          {repurposeRows.map((row) => (
            <div key={row.id} className="p-3 border border-white/10 bg-white/[0.02]">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-white font-semibold text-sm">{row.tool_name} - {row.content_type}</div>
                  <div className="text-xs text-text-secondary">{row.platform || 'multi-platform'} • {row.status}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => downloadText(row)} className="btn-secondary rounded-none inline-flex items-center gap-1 text-xs">
                    <Download size={12} /> Download
                  </button>

                  {String(row.content_type).includes('carousel') && (
                    <>
                      <button
                        onClick={() => makeCarouselMedia(row)}
                        disabled={workingId === row.id}
                        className="btn-secondary rounded-none inline-flex items-center gap-1 text-xs"
                      >
                        <Upload size={12} /> Make PDF
                      </button>
                      <button
                        onClick={() => previewCarousel(row)}
                        disabled={workingId === row.id}
                        className="btn-secondary rounded-none inline-flex items-center gap-1 text-xs"
                      >
                        <Eye size={12} /> Preview
                      </button>
                    </>
                  )}

                  {(String(row.content_type).includes('youtube') || row.platform === 'youtube') && (
                    <button
                      onClick={() => optimizeYouTube(row)}
                      disabled={workingId === row.id}
                      className="btn-secondary rounded-none inline-flex items-center gap-1 text-xs"
                    >
                      <Sparkles size={12} /> SEO Meta
                    </button>
                  )}

                  <button
                    onClick={() => publishItem(row)}
                    disabled={workingId === row.id || row.status === 'published'}
                    className="btn-primary rounded-none inline-flex items-center gap-1 text-xs"
                  >
                    <Play size={12} /> {row.status === 'published' ? 'Published' : 'Publish'}
                  </button>
                </div>
              </div>

              {row.carousel?.pdf_url && (
                <a href={row.carousel.pdf_url} target="_blank" rel="noreferrer" className="text-cyber-yellow text-xs underline mt-2 inline-block">
                  Open generated PDF media
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RepurposeManager;
